import { useState } from "react";
import { Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { API_ENDPOINTS } from "../config/api";
import { apiPost } from "../utils/apiClient";

function LeagueSetupWizard() {
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const { setCurrentLeague, refreshLeagues } = useLeague();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    const formData = new FormData(event.currentTarget);
    const name = formData.get("name") as string;
    const abbr = formData.get("abbr") as string;

    // Validation
    if (!name || name.length < 3) {
      setErrorMessage("League name must be at least 3 characters");
      setIsLoading(false);
      return;
    }

    if (!abbr || abbr.length < 2 || abbr.length > 5) {
      setErrorMessage("Abbreviation must be 2-5 characters");
      setIsLoading(false);
      return;
    }

    if (!/^[A-Z]+$/.test(abbr)) {
      setErrorMessage("Abbreviation must contain only uppercase letters");
      setIsLoading(false);
      return;
    }

    try {
      const result = await apiPost(
        API_ENDPOINTS.leagues,
        { league: { name, abbr } },
        { token: auth.token, skipLeagueHeader: true },
      );

      // Update league context
      setCurrentLeague(result);
      refreshLeagues();

      // Navigate to season setup (automatic redirect per requirements)
      navigate("/setup/season", { replace: true });
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "An error occurred";
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title>Create Your League</Card.Title>
        <Card.Text className="text-muted mb-4">
          A league is the organization that manages your seasons, teams, and
          players.
        </Card.Text>

        {errorMessage && <Alert variant="warning">{errorMessage}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formLeagueName">
            <Form.Label>League Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Enter league name"
              required
              disabled={isLoading}
            />
            <Form.Text className="text-muted">
              e.g., "Podium Hockey League"
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formLeagueAbbr">
            <Form.Label>Abbreviation</Form.Label>
            <Form.Control
              type="text"
              name="abbr"
              placeholder="Enter abbreviation"
              maxLength={5}
              required
              disabled={isLoading}
              style={{ textTransform: "uppercase" }}
            />
            <Form.Text className="text-muted">
              2-5 uppercase letters (e.g., "PODIUM")
            </Form.Text>
          </Form.Group>

          <div className="d-grid">
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              Create League
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default LeagueSetupWizard;
